import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, ChevronDown, RotateCcw, Printer } from "lucide-react";
import { printPdfBlob, preopenForIOS } from "@/lib/print";

export function TechChecklistSettingsCard() {
  const [secs, setSecs] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/tech-checklist/template").then(({ data }) => setSecs(data.sections)).catch(() => setSecs([])); }, []);
  const upd = (si, fn) => setSecs((p) => p.map((s, i) => (i === si ? fn(s) : s)));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/tech-checklist/template", { sections: secs }); setSecs(data.sections); toast.success("Checklist completo salvo"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Voltar para a lista padrão? Suas alterações serão perdidas.")) { return; }
    const { data } = await api.post("/tech-checklist/template/reset"); setSecs(data.sections); toast.success("Lista padrão restaurada");
  };
  const printBlank = async () => {
    const win = preopenForIOS();
    try { const { data } = await api.get("/tech-checklist/pdf", { responseType: "blob" }); printPdfBlob(data, win); }
    catch (e) { if (win) win.close(); toast.error(formatApiError(e)); }
  };
  if (!secs) { return null; }
  return (
    <section data-testid="tech-checklist-settings" className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-3">
      <div className="flex items-center gap-2 font-bold text-zinc-100"><ClipboardList className="h-4 w-4 text-green-brand" /> Checklist completo (inspeção técnica)</div>
      <p className="text-[11px] text-zinc-500">Seções e itens que aparecem no checklist completo da OS. Renomear um item não altera OS já preenchidas.</p>
      {secs.map((s, si) => (
        <div key={si} className="rounded-xl border border-zinc-800">
          <div className="flex items-center gap-2 p-2">
            <button type="button" onClick={() => setOpen((p) => ({ ...p, [si]: !p[si] }))} className="text-zinc-400" data-testid={`tcs-toggle-${si}`}><ChevronDown className={`h-4 w-4 transition-transform ${open[si] ? "rotate-180" : ""}`} /></button>
            <Input value={s.title} onChange={(e) => upd(si, (x) => ({ ...x, title: e.target.value }))} className="h-9 bg-zinc-900 border-zinc-800 font-bold" data-testid={`tcs-title-${si}`} />
            <span className="text-xs text-zinc-500 whitespace-nowrap">{s.items.length} itens</span>
            <button type="button" title="Remover seção" onClick={() => window.confirm(`Remover a seção "${s.title}"?`) && setSecs((p) => p.filter((_, i) => i !== si))} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
          {open[si] && (
            <div className="px-2 pb-2 space-y-1.5">
              {s.items.map((it, ii) => (
                <div key={ii} className="flex gap-2">
                  <Input value={it} onChange={(e) => upd(si, (x) => ({ ...x, items: x.items.map((v, j) => (j === ii ? e.target.value : v)) }))} className="h-8 bg-zinc-900 border-zinc-800 text-sm" data-testid={`tcs-item-${si}-${ii}`} />
                  <button type="button" title="Remover item" onClick={() => upd(si, (x) => ({ ...x, items: x.items.filter((_, j) => j !== ii) }))} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" data-testid={`tcs-add-item-${si}`} onClick={() => upd(si, (x) => ({ ...x, items: [...x.items, ""] }))} className="bg-zinc-900 border-zinc-800"><Plus className="h-3.5 w-3.5 mr-1" /> Item</Button>
            </div>
          )}
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" data-testid="tcs-add-section" onClick={() => { setSecs((p) => [...p, { title: "Nova seção", items: [] }]); setOpen((p) => ({ ...p, [secs.length]: true })); }} className="bg-zinc-900 border-zinc-800"><Plus className="h-3.5 w-3.5 mr-1" /> Seção</Button>
        <Button size="sm" data-testid="tcs-save" disabled={busy} onClick={save} className="bg-green-brand text-white">Salvar</Button>
        <Button size="sm" variant="outline" data-testid="tcs-print-blank" onClick={printBlank} className="bg-zinc-900 border-zinc-800"><Printer className="h-3.5 w-3.5 mr-1" /> Imprimir em branco</Button>
        <Button size="sm" variant="outline" data-testid="tcs-reset" onClick={reset} className="bg-zinc-900 border-zinc-800"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Restaurar padrão</Button>
      </div>
    </section>
  );
}
